import React, { useState, useEffect } from "react";
import { Edit2, Trash2, Plus, ChevronDown, ChevronUp } from "lucide-react";
import IndustryFormModal from "../../components/AdminSideComponent/Industries/Industryformmodal";
import DeleteConfirmModal from "../../components/AdminSideComponent/Common/Deleteconfirmmodal";

const API_BASE_URL = `${process.env.REACT_APP_API_URL}/industries`; // Change this to your backend URL
const PRODUCTS_API = `${process.env.REACT_APP_API_URL}/products`; // For fetching available products

const IndustryList = () => {
  const [industries, setIndustries] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [modalType, setModalType] = useState(null); // 'add', 'edit', 'delete', null
  const [selectedIndustry, setSelectedIndustry] = useState(null);
  const [expandedIndustry, setExpandedIndustry] = useState(null);

  // Fetch industries and products on mount
  useEffect(() => {
    fetchIndustries();
    fetchProducts();
  }, []);

  const fetchIndustries = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/`);
      if (!response.ok) throw new Error("Failed to fetch industries");
      const data = await response.json();
    //   console.log("first" , data.data);
      setIndustries(data.data || data);
    } catch (err) {
      setError(err.message);
      console.error("Error fetching industries:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await fetch(`${PRODUCTS_API}/getAll`);
      if (!response.ok) throw new Error("Failed to fetch products");
      const data = await response.json();
      setProducts(data.data || data);
    } catch (err) {
      console.error("Error fetching products:", err);
    }
  };

  const handleAddIndustry = () => {
    setSelectedIndustry(null);
    setModalType("add");
  };

  const handleEditIndustry = (industry) => {
    setSelectedIndustry(industry);
    setModalType("edit");
  };

  const handleDeleteIndustry = (industry) => {
    setSelectedIndustry(industry);
    setModalType("delete");
  };

  const handleCloseModal = () => {
    setModalType(null);
    setSelectedIndustry(null);
  };

  const handleIndustrySaved = () => {
    fetchIndustries();
    handleCloseModal();
  };

  const handleIndustryDeleted = () => {
    fetchIndustries();
    handleCloseModal();
  };

  const getProductName = (productId) => {
    const product = products.find((p) => p._id === productId);
    return product?.heading || "Unknown Product";
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-900">Industries</h1>
            <p className="text-gray-600 mt-2">Manage industry categories</p>
          </div>
          <button
            onClick={handleAddIndustry}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition"
          >
            <Plus size={20} />
            Add Industry
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        {/* Industries Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : industries.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
            <p className="text-gray-600 text-lg">No industries found</p>
          </div>
        ) : (
          <div className="grid gap-6">
            {industries.map((industry) => (
              <div
                key={industry._id}
                className="bg-white rounded-lg shadow hover:shadow-md transition border border-gray-200"
              >
                {/* Industry Header */}
                <div className="p-6 border-b border-gray-200 flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      {industry.icon && (
                        <div className="text-3xl">{industry.icon}</div>
                      )}
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900">
                          {industry.title}
                        </h3>
                        <p className="text-gray-600 text-sm mt-1">
                          {industry.description}
                        </p>
                      </div>
                    </div>
                    <div className="mt-3 flex items-center gap-4">
                      <span
                        className={`px-3 py-1 rounded-full text-sm font-semibold ${
                          industry.isActive
                            ? "bg-green-100 text-green-800"
                            : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {industry.isActive ? "Active" : "Inactive"}
                      </span>
                      <p className="text-sm text-gray-500">
                        Slug:{" "}
                        <code className="bg-gray-100 px-2 py-1 rounded">
                          {industry.slug}
                        </code>
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEditIndustry(industry)}
                      className="p-2 bg-blue-100 hover:bg-blue-200 text-blue-600 rounded-lg transition"
                      title="Edit"
                    >
                      <Edit2 size={20} />
                    </button>
                    <button
                      onClick={() => handleDeleteIndustry(industry)}
                      className="p-2 bg-red-100 hover:bg-red-200 text-red-600 rounded-lg transition"
                      title="Delete"
                    >
                      <Trash2 size={20} />
                    </button>
                    <button
                      onClick={() =>
                        setExpandedIndustry(
                          expandedIndustry === industry._id
                            ? null
                            : industry._id
                        )
                      }
                      className="p-2 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg transition"
                    >
                      {expandedIndustry === industry._id ? (
                        <ChevronUp size={20} />
                      ) : (
                        <ChevronDown size={20} />
                      )}
                    </button>
                  </div>
                </div>

                {/* Industry Details (Expandable) */}
                {expandedIndustry === industry._id && (
                  <div className="p-6 space-y-4 text-sm">
                    {industry.benefits?.length > 0 && (
                      <div>
                        <span className="font-semibold text-gray-700">
                          Benefits:
                        </span>
                        <ul className="mt-2 space-y-1">
                          {industry.benefits.map((benefit, idx) => (
                            <li
                              key={idx}
                              className="text-gray-600 flex items-center gap-2"
                            >
                              <span className="text-green-600">✓</span>
                              {benefit}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {industry.stats && (
                      <div>
                        <span className="font-semibold text-gray-700">
                          Metrics:
                        </span>
                        <div className="mt-2 bg-blue-50 p-3 rounded border border-blue-200">
                          <p className="text-2xl font-bold text-blue-600">
                            {industry.stats.metric}
                          </p>
                          <p className="text-gray-600">{industry.stats.label}</p>
                        </div>
                      </div>
                    )}

                    {industry.solutions?.length > 0 && (
                      <div>
                        <span className="font-semibold text-gray-700">
                          Related Solutions ({industry.solutions.length}):
                        </span>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {industry.solutions.map((solution, idx) => (
                            <span
                              key={idx}
                              className="bg-purple-100 text-purple-800 px-3 py-1 rounded text-xs"
                            >
                              {solution.productId.heading}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="text-xs text-gray-500">
                      Created: {new Date(industry.createdAt).toLocaleDateString()}
                      {industry.updatedAt !== industry.createdAt && (
                        <span>
                          {" "}
                          | Updated:{" "}
                          {new Date(industry.updatedAt).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      {(modalType === "add" || modalType === "edit") && (
        <IndustryFormModal
          mode={modalType}
          industry={selectedIndustry}
          products={products}
          onClose={handleCloseModal}
          onSave={handleIndustrySaved}
          apiBaseUrl={API_BASE_URL}
        />
      )}

      {modalType === "delete" && selectedIndustry && (
        <DeleteConfirmModal
          product={selectedIndustry}
          // itemType="Industry"
          onClose={handleCloseModal}
          onDelete={handleIndustryDeleted}
          apiBaseUrl={API_BASE_URL}
        />
      )}
    </div>
  );
};

export default IndustryList;